import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

import { isEffectiveMembership, useAuth } from '../../auth/AuthContext';
import type { CurrentUser, Role } from '../../api/users';
import { FAQS, FAQ_SECTIONS, type FaqEntry } from './faqContent';

/**
 * Every role the user actually holds, across all their organisations. The
 * page used to pick ONE role, which left coordinators with no answers at all
 * and showed someone who is both an admin and a participant only half of
 * what applies to them.
 */
export function userRoles(user: CurrentUser | null): Set<Role> {
  const roles = new Set<Role>();
  for (const m of user?.memberships ?? []) {
    if (isEffectiveMembership(m)) roles.add(m.role);
  }
  return roles;
}

export function entriesFor(roles: Set<Role>): FaqEntry[] {
  if (roles.size === 0) return [];
  return FAQS.filter((f) =>
    f.audience.some((a) => a === 'all' || roles.has(a)),
  );
}

/** Lower-case, bold markers stripped — what search matches against. */
function plain(text: string): string {
  return text.replace(/\*\*/g, '').toLowerCase();
}

export function matches(entry: FaqEntry, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = plain(`${entry.q} ${entry.a} ${entry.more ?? ''}`);
  return words.every((w) => hay.includes(w));
}

/** `**bold**` → <strong>. The only markup the content uses. */
export function renderRich(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

function Answer({ entry }: { entry: FaqEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {renderRich(entry.a)}
      </Typography>
      {entry.more &&
        (open ? (
          <Typography variant="body2" color="text.secondary">
            {renderRich(entry.more)}
          </Typography>
        ) : (
          <Button
            size="small"
            onClick={() => setOpen(true)}
            sx={{ alignSelf: 'flex-start', px: 0 }}
          >
            More detail
          </Button>
        ))}
    </Stack>
  );
}

export function Faq() {
  const { user } = useAuth();
  const roles = useMemo(() => userRoles(user), [user]);
  const entries = useMemo(() => entriesFor(roles), [roles]);
  const [query, setQuery] = useState('');
  const shown = useMemo(
    () => entries.filter((e) => matches(e, query)),
    [entries, query],
  );
  const searching = query.trim().length > 0;
  // A link to one answer (`/help/faq#faq-<id>`, e.g. from Getting Started)
  // opens that answer and scrolls to it.
  const { hash } = useLocation();
  const target = hash.startsWith('#faq-') ? hash.slice('#faq-'.length) : null;
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!target) return;
    setOpen((prev) => new Set(prev).add(target));
    document.getElementById(`faq-${target}`)?.scrollIntoView?.({ block: 'start' });
    // Keyed on the link alone: re-running on every re-render would re-add
    // the target and fight the reader closing it.
  }, [target]);
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Paper
      sx={{
        p: 3,
        // Same scroll envelope as Getting Started so a long section stays
        // readable inside the help shell.
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h5">Frequently asked questions</Typography>
        {roles.size === 0 ? (
          <Alert severity="info">
            Your account doesn't hold an active role yet. Sign out and back in,
            or ask your organisation's admin if this is unexpected.
          </Alert>
        ) : (
          <TextField
            size="small"
            placeholder="Search the questions — e.g. download, password, camera"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': 'Search the FAQ' },
            }}
          />
        )}
        {searching && shown.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing matches "{query.trim()}". Try a shorter word, or browse the
            sections below by clearing the search.
          </Typography>
        )}
        {FAQ_SECTIONS.map((section) => {
          const items = shown.filter((e) => e.section === section);
          if (items.length === 0) return null;
          return (
            <Stack key={section} spacing={1}>
              <Typography variant="h6">{section}</Typography>
              {items.map((f) => (
                <Accordion
                  key={f.id}
                  id={`faq-${f.id}`}
                  // While searching, the matches are the answer — open them.
                  expanded={searching || open.has(f.id)}
                  onChange={() => toggle(f.id)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1">{f.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Answer entry={f} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
